import { HttpApiGroup } from "@effect/platform";
import { PillTheme } from "@redotech/redo-storefront-common/pill-theme";
import { Schema } from "effect";
import { endpoint } from "../../auth/endpoint";
import { DataIntegrityError, DatabaseError } from "../../auth/errors";
import { ShopperAiTagGroupId, ShopperAiTagId } from "../../ids/definitions";
import { TagGroupResponse, TagResponse } from "../tag";
import {
  SystemConflict,
  SystemNotFound,
  SystemReferenceNotFound,
} from "./errors";
import { SystemRequestContext } from "./request-context";

const NullablePillTheme = Schema.NullOr(Schema.Enums(PillTheme));

export const SystemCreateTagGroupPayload = Schema.Struct({
  ...SystemRequestContext,
  name: Schema.String,
  description: Schema.optional(Schema.NullOr(Schema.String)),
  allowMultipleTags: Schema.Boolean,
  requiredTagBeforeClose: Schema.optional(Schema.Boolean),
});
export type SystemCreateTagGroupPayload =
  typeof SystemCreateTagGroupPayload.Type;

/** An omitted field keeps its value; `description: null` clears it. */
export const SystemUpdateTagGroupPayload = Schema.Struct({
  ...SystemRequestContext,
  tagGroupId: ShopperAiTagGroupId,
  patch: Schema.Struct({
    name: Schema.optional(Schema.String),
    description: Schema.optional(Schema.NullOr(Schema.String)),
    allowMultipleTags: Schema.optional(Schema.Boolean),
    requiredTagBeforeClose: Schema.optional(Schema.Boolean),
  }),
});
export type SystemUpdateTagGroupPayload =
  typeof SystemUpdateTagGroupPayload.Type;

export const SystemDeleteTagGroupPayload = Schema.Struct({
  ...SystemRequestContext,
  tagGroupId: ShopperAiTagGroupId,
});
export type SystemDeleteTagGroupPayload =
  typeof SystemDeleteTagGroupPayload.Type;

export const SystemCreateTagPayload = Schema.Struct({
  ...SystemRequestContext,
  tagGroupId: ShopperAiTagGroupId,
  name: Schema.String,
  pillTheme: Schema.optional(NullablePillTheme),
});
export type SystemCreateTagPayload = typeof SystemCreateTagPayload.Type;

/** An omitted field keeps its value; a `tagGroupId` moves the tag. */
export const SystemUpdateTagPayload = Schema.Struct({
  ...SystemRequestContext,
  tagId: ShopperAiTagId,
  patch: Schema.Struct({
    tagGroupId: Schema.optional(ShopperAiTagGroupId),
    name: Schema.optional(Schema.String),
    pillTheme: Schema.optional(NullablePillTheme),
  }),
});
export type SystemUpdateTagPayload = typeof SystemUpdateTagPayload.Type;

export const SystemDeleteTagPayload = Schema.Struct({
  ...SystemRequestContext,
  tagId: ShopperAiTagId,
});
export type SystemDeleteTagPayload = typeof SystemDeleteTagPayload.Type;

const TagGroupResult = Schema.Struct({ tagGroup: TagGroupResponse });
const TagResult = Schema.Struct({ tag: TagResponse });

/** Support tag writes for the public API; each mirrors the merchant tag endpoint of the same name. */
export const SystemSupportTagGroup = HttpApiGroup.make("systemSupportTag")
  .add(
    endpoint.system
      .post("createTagGroup", "/groups/create")
      .setPayload(SystemCreateTagGroupPayload)
      .addSuccess(TagGroupResult)
      .addError(SystemConflict)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.system
      .post("updateTagGroup", "/groups/update")
      .setPayload(SystemUpdateTagGroupPayload)
      .addSuccess(TagGroupResult)
      .addError(SystemConflict)
      .addError(SystemNotFound)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.system
      .post("deleteTagGroup", "/groups/delete")
      .setPayload(SystemDeleteTagGroupPayload)
      .addSuccess(Schema.Void)
      .addError(SystemNotFound)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.system
      .post("createTag", "/create")
      .setPayload(SystemCreateTagPayload)
      .addSuccess(TagResult)
      .addError(SystemConflict)
      .addError(SystemReferenceNotFound)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.system
      .post("updateTag", "/update")
      .setPayload(SystemUpdateTagPayload)
      .addSuccess(TagResult)
      .addError(SystemConflict)
      .addError(SystemNotFound)
      .addError(SystemReferenceNotFound)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.system
      .post("deleteTag", "/delete")
      .setPayload(SystemDeleteTagPayload)
      .addSuccess(Schema.Void)
      .addError(SystemNotFound)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .prefix("/system/support/tags");
